<template>
  <div class="note-meta-fields" :class="{ 'note-meta-fields--loading': loading }">
    <div class="note-meta-fields__item">
      <LabelDropdown
        label="Note Type"
        :select-id="`${selectIdPrefix}-note-type`"
        :model-value="noteType"
        flex
        wide
        :disabled="disabled || loading || (!noteTypeOptions.length && !allowCreate)"
        :options="noteTypeOptions"
        :menu-class="menuClass"
        :allow-create="allowCreate"
        :creating="creatingNoteType"
        @update:model-value="onNoteTypeChange"
        @create="onCreateNoteType"
      />
    </div>
    <div class="note-meta-fields__item">
      <LabelDropdown
        label="Priority"
        :select-id="`${selectIdPrefix}-priority`"
        :model-value="priority"
        flex
        wide
        :disabled="disabled || loading || (!priorityOptions.length && !allowCreate)"
        :options="priorityOptions"
        :menu-class="menuClass"
        :allow-create="allowCreate"
        :creating="creatingPriority"
        @update:model-value="onPriorityChange"
        @create="onCreatePriority"
      />
    </div>
  </div>
</template>

<script>
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import noteMetaService from '@/services/noteMeta.service.js';
import logger from '@/utils/logger';

export default {
  name: 'NoteTypePriorityFields',
  components: {
    LabelDropdown,
  },
  props: {
    companyId: {
      type: [String, Number],
      default: null,
    },
    noteType: {
      type: String,
      default: '',
    },
    priority: {
      type: String,
      default: '',
    },
    selectIdPrefix: {
      type: String,
      default: 'note-meta',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    menuClass: {
      type: [String, Array, Object],
      default: '',
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:noteType', 'update:priority', 'loaded', 'created'],
  data() {
    return {
      noteTypeOptions: [],
      priorityOptions: [],
      loading: false,
      creatingNoteType: false,
      creatingPriority: false,
    };
  },
  watch: {
    companyId: {
      handler() {
        this.loadOptions();
      },
      immediate: true,
    },
  },
  methods: {
    mapOptions(items) {
      return (items || []).map((item) => ({
        value: item.id,
        label: item.name,
      }));
    },
    pickDefault(options, preferredNames) {
      if (!options.length) return '';
      for (const name of preferredNames) {
        const match = options.find((opt) => opt.label === name);
        if (match) return match.value;
      }
      return options[0].value;
    },
    async loadOptions() {
      if (!this.companyId) {
        this.noteTypeOptions = [];
        this.priorityOptions = [];
        this.$emit('update:noteType', '');
        this.$emit('update:priority', '');
        return;
      }

      this.loading = true;
      try {
        const [typesRes, prioritiesRes] = await Promise.all([
          noteMetaService.getNoteTypes(this.companyId),
          noteMetaService.getNotePriorities(this.companyId),
        ]);

        this.noteTypeOptions = this.mapOptions(typesRes?.data?.data);
        this.priorityOptions = this.mapOptions(prioritiesRes?.data?.data);

        const matchesValue = (options, current) =>
          options.some((opt) => String(opt.value) === String(current));

        const nextNoteType = matchesValue(this.noteTypeOptions, this.noteType)
          ? this.noteType
          : this.pickDefault(this.noteTypeOptions, ['General', 'Meeting']);

        const nextPriority = matchesValue(this.priorityOptions, this.priority)
          ? this.priority
          : this.pickDefault(this.priorityOptions, ['Normal', 'Low']);

        if (nextNoteType !== this.noteType) {
          this.$emit('update:noteType', nextNoteType);
        }
        if (nextPriority !== this.priority) {
          this.$emit('update:priority', nextPriority);
        }

        this.$emit('loaded', {
          noteTypeOptions: this.noteTypeOptions,
          priorityOptions: this.priorityOptions,
        });
      } catch (error) {
        logger.error('Failed to load note type/priority lists:', error);
        this.noteTypeOptions = [];
        this.priorityOptions = [];
        this.$emit('update:noteType', '');
        this.$emit('update:priority', '');
      } finally {
        this.loading = false;
      }
    },
    onNoteTypeChange(value) {
      this.$emit('update:noteType', value || '');
    },
    onPriorityChange(value) {
      this.$emit('update:priority', value || '');
    },
    async onCreateNoteType(name) {
      if (!this.companyId || this.creatingNoteType) return;
      this.creatingNoteType = true;
      try {
        const response = await noteMetaService.createNoteType({
          name,
          companyId: this.companyId,
        });
        const item = response?.data?.data || response?.data;
        if (item?.id) {
          const option = { value: item.id, label: item.name || name };
          if (!this.noteTypeOptions.some((opt) => String(opt.value) === String(item.id))) {
            this.noteTypeOptions = [...this.noteTypeOptions, option].sort((a, b) =>
              String(a.label).localeCompare(String(b.label))
            );
          }
          this.$emit('update:noteType', item.id);
          this.$emit('created', { kind: 'noteType', item });
        }
      } catch (error) {
        logger.error('Failed to create note type:', error);
      } finally {
        this.creatingNoteType = false;
      }
    },
    async onCreatePriority(name) {
      if (!this.companyId || this.creatingPriority) return;
      this.creatingPriority = true;
      try {
        const response = await noteMetaService.createNotePriority({
          name,
          companyId: this.companyId,
        });
        const item = response?.data?.data || response?.data;
        if (item?.id) {
          const option = { value: item.id, label: item.name || name };
          if (!this.priorityOptions.some((opt) => String(opt.value) === String(item.id))) {
            this.priorityOptions = [...this.priorityOptions, option].sort((a, b) =>
              String(a.label).localeCompare(String(b.label))
            );
          }
          this.$emit('update:priority', item.id);
          this.$emit('created', { kind: 'priority', item });
        }
      } catch (error) {
        logger.error('Failed to create note priority:', error);
      } finally {
        this.creatingPriority = false;
      }
    },
  },
};
</script>

<style src="@/styles/NoteTypePriorityFields.css"></style>
